import { Draft, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DeviceType, FaultStatus, GpsFix, SoftwareStatus, YesNo } from '../apis/assetsApi';
import { IgnitionMinimum } from '../apis/healthApi';
import { Hours } from '../types';
import { authApi, MeasurementUnit } from '../apis/authApi';
import { getFilterNamesFromFiltersList, getFiltersList } from '@customer/filters/utils';
import { FilterGroup } from '@customer/filters/types';
import { FaultLevel } from './globalSlice';
import { getFaultsInFaultCategories } from '@customer/side-panels/filter-panel/filters/FaultsFilter';
import { GIType, VIType } from '../apis/interactionsApi';
import { FilterableFenceType } from '../apis/geofencesApi';
import {
  DocumentFamily,
  DocumentProduct,
  PartnerResourceFileType,
  TechLibraryDocumentType,
} from '@cas/config/documents';
import {
  distanceFromMetresConversion,
  distanceToMetresConversion,
  DistanceUnit,
  speedConversion,
  SpeedUnit,
} from '@cas/utils/numberHelpers';
import { AssetInvestigationType, InvestigationStatus } from '../apis/investigationsApi';

export const FILTERS_SLICE_NAME = 'filters';

// TODO: Increment this number whenever the filter types have been modified.
// This is to make sure that any outdated local storage is not loaded into the slice.
const FILTERS_VERSION = 6;

export interface MultiselectFilters {
  assetGroups: string[];
  assetGroupsRo: string[];
  configGroups: string[];
  deviceTypes: DeviceType[];
  faultCategories: number[];
  faultLevels: FaultLevel[];
  faults: number[];
  geofenceAreas: number[];
  geofenceInteractionTypes: GIType[];
  geofenceTypes: FilterableFenceType[];
  investigationTypes: AssetInvestigationType[];
  investigationStatuses: InvestigationStatus[];
  objectTypes: number[];
  objectTypesRo: number[];
  partnerResourceTypes: TechLibraryDocumentType[];
  partnerResourceFileTypes: PartnerResourceFileType[];
  techLibDocumentTypes: TechLibraryDocumentType[];
  techLibProducts: DocumentProduct[];
  techLibProductFamilies: DocumentFamily[];
  vehicleInteractionTypes: VIType[];
}

export interface SingleSelectFilters {
  audible: YesNo;
  faultStatus: FaultStatus;
  gpsFix: GpsFix;
  softwareStatus: SoftwareStatus;
  ignitionMinimum: IgnitionMinimum;
}

export interface NumberFilters {
  maxSpeedLo: number | null;
  minSpeedLo: number | null;
  minSpeedRo: number | null;
  maxDistance: number | null;
  minDuration: number | null;
  minTravelledDistance: number | null;
}

export interface FiltersState extends MultiselectFilters, SingleSelectFilters, NumberFilters {
  timeOfDayStart: Hours | null;
  timeOfDayEnd: Hours | null;
}

export interface FiltersSliceState {
  draft: FiltersState;
  saved: FiltersState;
  hasFilters: boolean;
  units: MeasurementUnit;
}

const initialFiltersState: FiltersState = {
  assetGroups: [],
  assetGroupsRo: [],
  audible: YesNo.All,
  configGroups: [],
  deviceTypes: [],
  faultCategories: [],
  faultLevels: [],
  faults: [],
  faultStatus: FaultStatus.All,
  geofenceAreas: [],
  geofenceInteractionTypes: [],
  geofenceTypes: [],
  gpsFix: GpsFix.All,
  ignitionMinimum: IgnitionMinimum.OneHour,
  investigationTypes: [],
  investigationStatuses: [],
  maxDistance: null,
  maxSpeedLo: null,
  minDuration: null,
  minSpeedLo: null,
  minSpeedRo: null,
  minTravelledDistance: null,
  objectTypes: [],
  objectTypesRo: [],
  partnerResourceTypes: [],
  partnerResourceFileTypes: [],
  softwareStatus: SoftwareStatus.All,
  techLibDocumentTypes: [],
  techLibProducts: [],
  techLibProductFamilies: [],
  timeOfDayEnd: null,
  timeOfDayStart: null,
  vehicleInteractionTypes: [],
};

interface PersistedFiltersState {
  version: number;
  saved: FiltersState;
}

// Type guard func that performs a shallow type check of the local storage data shape.
function isPersistedFilterState(data: unknown): data is PersistedFiltersState {
  return (
    data instanceof Object &&
    'version' in data &&
    typeof data.version === 'number' &&
    'saved' in data
  );
}

// Prevent showing ridiculous numbers of decimal points in filters
const round = (value: number) => {
  const multiplier = Math.pow(10, 1);
  return Math.round(value * multiplier) / multiplier;
};

const loadSpeedWithRounding = (value: number | null, speedUnit: SpeedUnit) => {
  if (value === null) {
    return null;
  }

  return round(speedConversion(value, SpeedUnit.mps, speedUnit));
};

const loadDistanceWithRounding = (value: number | null, distanceUnit: DistanceUnit) => {
  if (value === null) {
    return null;
  }

  return round(distanceFromMetresConversion(value, distanceUnit));
};

const saveSpeedWithRounding = (value: number | null, speedUnit: SpeedUnit) => {
  if (value === null) {
    return null;
  }

  return round(speedConversion(value, speedUnit, SpeedUnit.mps));
};

const saveDistanceWithRounding = (value: number | null, distanceUnit: DistanceUnit) => {
  if (value === null) {
    return null;
  }

  return round(distanceToMetresConversion(value, distanceUnit));
};

const draftToSaved = (draft: FiltersState, units: MeasurementUnit): FiltersState => {
  const { maxDistance, maxSpeedLo, minTravelledDistance, minSpeedLo, minSpeedRo, ...saved } = draft;

  const speedUnit = units === MeasurementUnit.Metric ? SpeedUnit.kmph : SpeedUnit.mph;
  const distanceUnit = units === MeasurementUnit.Metric ? DistanceUnit.Metres : DistanceUnit.Feet;

  return {
    ...saved,
    maxDistance: saveDistanceWithRounding(maxDistance, distanceUnit),
    maxSpeedLo: saveSpeedWithRounding(maxSpeedLo, speedUnit),
    minTravelledDistance: saveDistanceWithRounding(minTravelledDistance, distanceUnit),
    minSpeedLo: saveSpeedWithRounding(minSpeedLo, speedUnit),
    minSpeedRo: saveSpeedWithRounding(minSpeedRo, speedUnit),
  };
};

const savedToDraft = (saved: FiltersState, units: MeasurementUnit): FiltersState => {
  // We always save in a standard format (usually metres-based)
  const { maxDistance, maxSpeedLo, minTravelledDistance, minSpeedLo, minSpeedRo, ...draft } = saved;

  const speedUnit = units === MeasurementUnit.Metric ? SpeedUnit.kmph : SpeedUnit.mph;
  const distanceUnit = units === MeasurementUnit.Metric ? DistanceUnit.Metres : DistanceUnit.Feet;

  return {
    ...draft,
    maxDistance: loadDistanceWithRounding(maxDistance, distanceUnit),
    maxSpeedLo: loadSpeedWithRounding(maxSpeedLo, speedUnit),
    minTravelledDistance: loadDistanceWithRounding(minTravelledDistance, distanceUnit),
    minSpeedLo: loadSpeedWithRounding(minSpeedLo, speedUnit),
    minSpeedRo: loadSpeedWithRounding(minSpeedRo, speedUnit),
  };
};

// Determine if the draft values are different from the intitial state values.
const getHasFilters = (draft: FiltersState): boolean => {
  return Object.keys(initialFiltersState).some((key: string) => {
    const filterKey: keyof FiltersState = key as keyof FiltersState;
    const draftValue = draft[filterKey];
    const defaultValue = defaultState.draft[filterKey];

    if (Array.isArray(draftValue) && Array.isArray(defaultValue)) {
      // Compare array contents
      return (
        draftValue.length !== defaultValue.length ||
        draftValue.some((v, i) => v !== defaultValue[i])
      );
    }
    return draftValue !== defaultValue;
  });
};

const getLocalStorageKey = (siteId: string) => `${FILTERS_SLICE_NAME}-${siteId}`;

const getPersistedData = (siteId: string): FiltersState | null => {
  const key = getLocalStorageKey(siteId);
  const hasKey = Object.keys(window.localStorage).some(k => k === key);

  if (hasKey) {
    try {
      const item = window.localStorage.getItem(key);

      if (item) {
        const data: unknown = JSON.parse(item);

        // Check the data is a valid shape and matches the current version number of the filters.
        if (data && isPersistedFilterState(data) && data.version === FILTERS_VERSION) {
          return data.saved;
        }
      }
    } catch {
      // Swallow
    }
  }

  return null;
};

const writePersistedData = (saved: FiltersState, siteId: string) => {
  try {
    const data: PersistedFiltersState = { version: FILTERS_VERSION, saved };
    window.localStorage.setItem(getLocalStorageKey(siteId), JSON.stringify(data));
  } catch {
    // Swallow
  }
};

const saveAndPersist = (state: Draft<FiltersSliceState>, siteId: string) => {
  // Convert the draft filters to the corresponding data type for the saved state.
  state.saved = draftToSaved(state.draft, state.units);

  // Check if at least one filter has been applied
  state.hasFilters = getHasFilters(state.draft);

  // Save to local storage
  writePersistedData(state.saved, siteId);
};

export const defaultState: FiltersSliceState = {
  saved: { ...initialFiltersState },
  draft: { ...initialFiltersState },
  hasFilters: false,
  units: MeasurementUnit.Metric,
};

export interface TechLibFilterAction {
  docTypes?: TechLibraryDocumentType[];
  products?: DocumentProduct[];
  families?: DocumentFamily[];
  siteId: string;
}

const filtersSlice = createSlice({
  name: FILTERS_SLICE_NAME,
  initialState: { ...defaultState } as FiltersSliceState,
  reducers: {
    setAssetGroups: (state, action: PayloadAction<string[]>) => {
      state.draft.assetGroups = action.payload;
    },
    setAssetGroupsRo: (state, action: PayloadAction<string[]>) => {
      state.draft.assetGroupsRo = action.payload;
    },
    setVehicleInteractionTypes: (state, action: PayloadAction<VIType[]>) => {
      state.draft.vehicleInteractionTypes = action.payload;
    },
    setGeofenceAreas: (state, action: PayloadAction<number[]>) => {
      state.draft.geofenceAreas = action.payload;
    },
    setGeofenceInteractionTypes: (state, action: PayloadAction<GIType[]>) => {
      state.draft.geofenceInteractionTypes = action.payload;
    },
    setGeofenceTypes: (state, action: PayloadAction<FilterableFenceType[]>) => {
      state.draft.geofenceTypes = action.payload;
    },
    setGpsFix: (state, action: PayloadAction<GpsFix>) => {
      state.draft.gpsFix = action.payload;
    },
    setInvestigationTypes: (state, action: PayloadAction<AssetInvestigationType[]>) => {
      state.draft.investigationTypes = action.payload;
    },
    setInvestigationStatuses: (state, action: PayloadAction<InvestigationStatus[]>) => {
      state.draft.investigationStatuses = action.payload;
    },
    setAudible: (state, action: PayloadAction<YesNo>) => {
      state.draft.audible = action.payload;
    },
    setMaxSpeedLo: (state, action: PayloadAction<number | null>) => {
      state.draft.maxSpeedLo = action.payload;
    },
    setMinSpeedLo: (state, action: PayloadAction<number | null>) => {
      state.draft.minSpeedLo = action.payload;
    },
    setMinSpeedRo: (state, action: PayloadAction<number | null>) => {
      state.draft.minSpeedRo = action.payload;
    },
    setMaxDistance: (state, action: PayloadAction<number | null>) => {
      state.draft.maxDistance = action.payload;
    },
    setMinDuration: (state, action: PayloadAction<number | null>) => {
      state.draft.minDuration = action.payload;
    },
    setMinTravelledDistance: (state, action: PayloadAction<number | null>) => {
      state.draft.minTravelledDistance = action.payload;
    },
    setFaultLevels: (state, action: PayloadAction<FaultLevel[]>) => {
      state.draft.faultLevels = action.payload;
    },
    // The setFaultCategories reducer also affects the 'faults' filter.
    setFaultCategories: (state, action: PayloadAction<number[]>) => {
      state.draft.faultCategories = action.payload;

      // Filter out any faults that do not belong to the selected fault categories
      const validFaultIds = getFaultsInFaultCategories(state.draft.faultCategories);
      const validFaultSelection = state.draft.faults.filter(id => validFaultIds.includes(id));
      state.draft.faults = validFaultSelection;
    },
    setFaults: (state, action: PayloadAction<number[]>) => {
      state.draft.faults = action.payload;
    },
    setFaultStatus: (state, action: PayloadAction<FaultStatus>) => {
      state.draft.faultStatus = action.payload;
    },
    setDeviceTypes: (state, action: PayloadAction<DeviceType[]>) => {
      state.draft.deviceTypes = action.payload;
    },
    setConfigGroups: (state, action: PayloadAction<string[]>) => {
      state.draft.configGroups = action.payload;
    },
    setSoftwareStatus: (state, action: PayloadAction<SoftwareStatus>) => {
      state.draft.softwareStatus = action.payload;
    },
    setIgnitionMinimum: (state, action: PayloadAction<IgnitionMinimum>) => {
      state.draft.ignitionMinimum = action.payload;
    },
    setObjectTypes: (state, action: PayloadAction<number[]>) => {
      state.draft.objectTypes = action.payload;
    },
    setObjectTypesRo: (state, action: PayloadAction<number[]>) => {
      state.draft.objectTypesRo = action.payload;
    },
    setPartnerResourceTypes: (state, action: PayloadAction<TechLibraryDocumentType[]>) => {
      state.draft.partnerResourceTypes = action.payload;
    },
    setPartnerResourceFileTypes: (state, action: PayloadAction<PartnerResourceFileType[]>) => {
      state.draft.partnerResourceFileTypes = action.payload;
    },
    setTechLibDocumentTypes: (state, action: PayloadAction<TechLibraryDocumentType[]>) => {
      state.draft.techLibDocumentTypes = action.payload;
    },
    setTechLibFilters: (state, action: PayloadAction<TechLibFilterAction>) => {
      state.draft.techLibDocumentTypes = action.payload.docTypes ?? [];
      state.draft.techLibProductFamilies = action.payload.families ?? [];
      state.draft.techLibProducts = action.payload.products ?? [];

      saveAndPersist(state, action.payload.siteId);
    },
    setTechLibProducts: (state, action: PayloadAction<DocumentProduct[]>) => {
      state.draft.techLibProducts = action.payload;
    },
    setTechLibProductFamilies: (state, action: PayloadAction<DocumentFamily[]>) => {
      state.draft.techLibProductFamilies = action.payload;
    },
    setTimeOfDayEnd: (state, action: PayloadAction<Hours>) => {
      state.draft.timeOfDayEnd = action.payload;
    },
    setTimeOfDayStart: (state, action: PayloadAction<Hours>) => {
      state.draft.timeOfDayStart = action.payload;
    },
    saveFilters: (state, action: PayloadAction<string>) => {
      const siteId = action.payload;

      saveAndPersist(state, siteId);
    },
    clearDraftFilters: state => ({
      ...state,
      draft: {
        ...initialFiltersState,
      },
    }),
    clearFiltersSubset: (
      state,
      action: PayloadAction<{ filterGroup: FilterGroup; siteId: string }>,
    ) => {
      const { filterGroup, siteId } = action.payload;

      // Get all of the filter keys that belong to the subset.
      const filterNames = getFilterNamesFromFiltersList(getFiltersList(filterGroup));

      // Copy the whole initial filters state to a new object.
      const initialFiltersSubset: Partial<FiltersState> = {
        ...initialFiltersState,
      };

      // Delete the default filter properties that are not part of the filters subset.
      for (const key in Object.keys(state.draft)) {
        const filterName = key as keyof FiltersState;
        if (!filterNames.includes(filterName)) {
          delete initialFiltersSubset[filterName];
        }
      }

      // Merge the current draft state and a subset of the initial filter values.
      state.draft = {
        ...state.draft,
        ...initialFiltersSubset,
      };

      // Update the saved and hasFilters state.
      saveAndPersist(state, siteId);
    },
    // Restore the draft filters to match the saved state
    resetDraftFilters: state => {
      state.draft = savedToDraft(state.saved, state.units);
    },
  },
  extraReducers: builder => {
    // When the auth process has succeeded, use the siteId to load any user filters that have been saved in local storage.
    builder.addMatcher(authApi.endpoints.me.matchFulfilled, (state, { payload }) => {
      // Only load the saved filters that match the current site id from local storage
      if (typeof payload.currentSite?.id !== 'undefined') {
        const saved: FiltersState | null = getPersistedData(payload.currentSite.id);

        if (saved) {
          // Convert the saved values to valid draft values.
          const draft = savedToDraft(saved, payload.units);

          // Update the 'hasFilters' property based on the new saved/draft state.
          const hasFilters = getHasFilters(draft);

          return {
            saved,
            draft,
            hasFilters,
            units: payload.units,
          };
        }
      }
    });
  },
});

export const { reducer: filtersReducer, actions: filtersActions } = filtersSlice;

export const {
  setAssetGroups,
  setAssetGroupsRo,
  setAudible,
  setConfigGroups,
  setDeviceTypes,
  setFaultCategories,
  setFaultLevels,
  setFaults,
  setFaultStatus,
  setGeofenceAreas,
  setGeofenceInteractionTypes,
  setGeofenceTypes,
  setGpsFix,
  setIgnitionMinimum,
  setInvestigationTypes,
  setInvestigationStatuses,
  setMaxDistance,
  setMaxSpeedLo,
  setMinDuration,
  setMinSpeedLo,
  setMinSpeedRo,
  setMinTravelledDistance,
  setObjectTypes,
  setObjectTypesRo,
  setPartnerResourceTypes,
  setPartnerResourceFileTypes,
  setSoftwareStatus,
  setVehicleInteractionTypes,
  setTechLibDocumentTypes,
  setTechLibFilters,
  setTechLibProducts,
  setTechLibProductFamilies,
  setTimeOfDayEnd,
  setTimeOfDayStart,
  saveFilters,
  clearDraftFilters,
  clearFiltersSubset,
  resetDraftFilters,
} = filtersActions;

export default filtersSlice;
